import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import UpdateExpectedDateModal from '../components/UpdateExpectedDateModal';

interface VendorRow {
  id: number;
  vendor: string;
  location: string;
}

interface PORow {
  id: number;
  poNumber: string;
  expected: string;
  ordered: number;
  received: number;
  hot: boolean;
}

const MOCK_VENDORS: VendorRow[] = [
  { id: 1, vendor: 'AAON Inc.', location: 'WH-A' },
  { id: 2, vendor: 'Carrier Corporation', location: 'WH-B' },
  { id: 3, vendor: 'Trane Technologies', location: 'WH-A' },
  { id: 4, vendor: 'Daikin Applied Americas', location: 'WH-C' },
  { id: 5, vendor: 'York / Johnson Controls', location: 'WH-B' },
  { id: 6, vendor: 'Lennox International', location: 'WH-A' },
];

const MOCK_POS: Record<number, PORow[]> = {
  1: [
    { id: 101, poNumber: 'PO-2026-0441', expected: '2026-03-18', ordered: 4, received: 1, hot: true },
    { id: 102, poNumber: 'PO-2026-0442', expected: '2026-03-19', ordered: 2, received: 0, hot: false },
    { id: 103, poNumber: 'PO-2026-0443', expected: '2026-03-21', ordered: 8, received: 5, hot: false },
  ],
  2: [
    { id: 201, poNumber: 'PO-2026-0550', expected: '2026-03-19', ordered: 6, received: 3, hot: false },
    { id: 202, poNumber: 'PO-2026-0551', expected: '2026-03-20', ordered: 1, received: 0, hot: false },
  ],
  3: [
    { id: 301, poNumber: 'PO-2026-0610', expected: '2026-03-20', ordered: 3, received: 0, hot: false },
  ],
  4: [
    { id: 401, poNumber: 'PO-2026-0720', expected: '2026-03-17', ordered: 2, received: 0, hot: true },
    { id: 402, poNumber: 'PO-2026-0721', expected: '2026-03-18', ordered: 5, received: 2, hot: true },
  ],
  5: [
    { id: 501, poNumber: 'PO-2026-0800', expected: '2026-03-21', ordered: 4, received: 4, hot: false },
  ],
  6: [
    { id: 601, poNumber: 'PO-2026-0901', expected: '2026-03-22', ordered: 10, received: 0, hot: false },
  ],
};

const conditionalRowStyles = [
  {
    when: (row: PORow) => row.hot,
    style: {
      backgroundColor: 'rgba(255, 200, 0, 0.3)',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(255, 200, 0, 0.45)',
      },
    },
  },
];

const formatDisplayDate = (isoDate: string) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${month}/${day}/${year}`;
};

const WarehousePickupVendorPOsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PORow | null>(null);

  const numericVendorId = parseInt(vendorId, 10);
  const vendor = MOCK_VENDORS.find((v) => v.id === numericVendorId);
  const pos = MOCK_POS[numericVendorId] || [];

  const handleExpectedClick = (row: PORow, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPO(row);
    setModalOpen(true);
  };

  const columns: IDataTableColumn<PORow>[] = [
    {
      name: 'Expected',
      selector: 'expected',
      sortable: true,
      grow: 0.8,
      cell: (row: PORow) => (
        <Button
          variant="text"
          size="small"
          sx={{ textDecoration: 'underline', p: 0, minWidth: 'auto', fontWeight: row.hot ? 'bold' : 'normal' }}
          onClick={(e) => handleExpectedClick(row, e)}
        >
          {formatDisplayDate(row.expected)}
        </Button>
      ),
    },
    {
      name: 'PO Number',
      selector: 'poNumber',
      sortable: true,
      grow: 1,
    },
    {
      name: 'Order / Receive',
      selector: 'ordered',
      sortable: false,
      grow: 0.8,
      cell: (row: PORow) => (
        <span>
          {row.ordered} / {row.received}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: 'hot',
      sortable: false,
      grow: 0.6,
      cell: (row: PORow) =>
        row.hot ? <Chip label="Urgent" color="warning" size="small" /> : <Chip label="Normal" color="default" size="small" variant="outlined" />,
    },
    {
      name: 'Actions',
      selector: 'id',
      sortable: false,
      grow: 1.2,
      cell: (row: PORow) => (
        <>
          <Button
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/employee_portal/warehouse_pickup/pos/${row.id}/details`);
            }}
          >
            View Details
          </Button>
          <Tooltip title="Receiving not yet available">
            <span>
              <Button variant="outlined" size="small" disabled>
                Receive
              </Button>
            </span>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button variant="text" onClick={() => history.push('/employee_portal/warehouse_pickup')} sx={{ mb: 1, pl: 0 }}>
            ← Back to Vendor List
          </Button>
          <Typography variant="h5" fontWeight="bold">
            Open POs — {vendor?.vendor ?? `Vendor #${vendorId}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Location: {vendor?.location}. Click an expected date to update it. Yellow rows are urgent.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DataTable
            columns={columns}
            data={pos}
            highlightOnHover
            striped
            dense={false}
            conditionalRowStyles={conditionalRowStyles}
            noHeader
            responsive
          />
        </Grid>
      </Grid>

      {selectedPO && (
        <UpdateExpectedDateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          poNumber={selectedPO.poNumber}
          currentExpected={selectedPO.expected}
        />
      )}
    </>
  );
};

export default WarehousePickupVendorPOsPage;
